import * as i0 from '@angular/core';
import { Injectable, Inject, Component, inject } from '@angular/core';
import * as ForgRockIdentityProvider from '@forgerock/javascript-sdk';
import { defer } from 'rxjs';
import { jwtDecode } from 'jwt-decode';
import { getUnixTime, differenceInMilliseconds } from 'date-fns';
import { DOCUMENT } from '@angular/common';
import * as i3 from '@angular/router';

/**
 * Used to share user state between components
 */
class UserService {
    /**
     * State representing whether the user is authenticated or not
     */
    isAuthenticated = false;
    /**
     * State repreesnting previously retrieved user information
     */
    info;
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.0", ngImport: i0, type: UserService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.0", ngImport: i0, type: UserService, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.0", ngImport: i0, type: UserService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

class CookieService {
    document;
    constructor(document) {
        this.document = document;
    }
    getAll() {
        return Object.fromEntries(this.document.cookie
            .split('; ')
            .map(v => v.split(/=(.*)/s).map(decodeURIComponent)));
    }
    get(name) {
        const value = `; ${this.document.cookie}`;
        const parts = value.split(`; ${name}=`);
        if (parts.length === 2) {
            return parts.pop()?.split(';').shift();
        }
        return undefined;
    }
    set(name, value, domain) {
        const cookieDomain = domain
            ? ` domain=${domain};`
            : '';
        this.document.cookie = `${name}=${value ?? ''}; path=/;${cookieDomain}`;
    }
    has(name) {
        return Object.keys(this.getAll()).includes(name);
    }
    erase(name, domain) {
        this.document.cookie = `${name}=; Max-Age=-99999999; path=/; domain=${domain};`;
    }
    eraseAll(domain) {
        const cookies = this.document.cookie.split(';');
        for (const element of cookies) {
            this.erase(element.trim().split('=')[0], domain);
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.0", ngImport: i0, type: CookieService, deps: [{ token: DOCUMENT }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.0", ngImport: i0, type: CookieService, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.0", ngImport: i0, type: CookieService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [{ type: Document, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }] });

class AuthLibService {
    userService;
    cookieService;
    router;
    tokens = {
        accessToken: '',
        idToken: '',
        refreshToken: '',
        tokenExpiry: undefined,
    };
    hasRetried = false;
    tokenUpdater = {
        intervalTime: 250000,
        tokenUpdatePromise: null,
    };
    constructor(userService, cookieService, router) {
        this.userService = userService;
        this.cookieService = cookieService;
        this.router = router;
        this.createTokenRefreshInterval();
    }
    createTokenRefreshInterval() {
        this.tokenUpdater.intervalRef = window.setInterval(() => {
            // Attempting to retrieve the token via this method will cause the token
            // to be updated if necessary.  We can discard the return value here
            this.getTokenWithRefresh();
        }, this.tokenUpdater.intervalTime);
    }
    getTokenWithRefresh() {
        if (!this.getToken()) {
            // Various methods in the applications call this on things like, for example,
            // changing route within the application. These trigger when going through the
            // auth flow before user is authenticated. It doesn't cause problems as such
            // but will log out nasty looking errors to the console as there are no tokens
            // to parse for token expiry etc. In that case, just resolve with empty string
            return new Promise(resolve => resolve(''));
        }
        const tokenExpTime = this.getTokenExp(); // in seconds
        const now = getUnixTime(Date.now()); // convert time in ms to seconds
        const remainingTime = differenceInMilliseconds(tokenExpTime, now);
        // The token will expire in less than 60 seconds, so try to refresh it
        if (remainingTime <= 60) {
            return this.updateAndGetAccessToken();
        }
        // Otherwise just return the existing valid token
        return new Promise(resolve => resolve(this.getAppToken()));
    }
    // Immediately updates the access token and returns a promise that will resolve with that token
    updateAndGetAccessToken() {
        // If there is already another call to refresh the access token in flight, just return
        // a reference to that one, rather than create another request
        if (this.tokenUpdater.tokenUpdatePromise !== null) {
            return this.tokenUpdater.tokenUpdatePromise;
        }
        // No existing request already in flight, so make a new request
        this.tokenUpdater.tokenUpdatePromise = (async () => {
            return await this.getTokensForScramWithRetry();
        })();
        return this.tokenUpdater.tokenUpdatePromise;
    }
    async getTokensForScramWithRetry() {
        try {
            const tokens = await ForgRockIdentityProvider.TokenManager.getTokens({
                login: 'redirect',
                forceRenew: true,
            });
            this.tokens = tokens;
            this.tokenUpdater.tokenUpdatePromise = null;
            this.hasRetried = false;
            return this.tokens.accessToken;
        }
        catch (err) {
            if (!this.hasRetried) {
                this.hasRetried = true;
                console.info('Auth service: Failed to refresh access token for SCRAM, retrying');
                return await this.getTokensForScramWithRetry();
            }
            else {
                this.tokenUpdater.tokenUpdatePromise = null;
                this.hasRetried = false;
                await this.logoutwithoutpath();
                console.error(`Auth service: Failed to refresh access token for SCRAM ${err}`);
                throw err;
            }
        }
    }
    // Synchronous method to fetch the access token
    getAppToken() {
        return this.tokens.accessToken;
    }
    async getToken() {
        this.tokens = (await ForgRockIdentityProvider.TokenStorage.get());
        return this.tokens;
    }
    async getCurrentUser() {
        const user = (await ForgRockIdentityProvider.UserManager.getCurrentUser());
        this.userService.info = user;
        this.userService.isAuthenticated = true;
        return user;
    }
    async authenticate() {
        this.tokens = (await ForgRockIdentityProvider.TokenManager.getTokens({
            login: 'redirect',
        }));
    }
    async authorize(code, state) {
        this.tokens = await ForgRockIdentityProvider.TokenManager.getTokens({ query: { code, state } });
        return Promise.resolve(true);
    }
    ;
    async logout(hasExternalLogoutUri, path) {
        this.clearTokenRefreshIntervalAndTokens();
        try {
            await ForgRockIdentityProvider.FRUser.logout();
            this.userService.isAuthenticated = false;
            this.userService.info = null;
        }
        catch (error) {
            // throw new Error(error.message);
        }
        this.clearLocalStorageAndCookies();
        await this.logoutRedirection(hasExternalLogoutUri, path);
    }
    async logoutRedirection(hasExternalLogoutUri, logoutUrl) {
        if (hasExternalLogoutUri) {
            // This custom redirect is used when an application does not have its
            // own logged out page. The user is redirected to the Identity Provider
            location.assign(`${document.location.origin}/` + logoutUrl);
        }
        else {
            // Application has its own logged out page, so navigate to that
            // For hash based routing, we need to remove the hash and leading slash
            await this.router.navigate([logoutUrl.startsWith('#/') ? logoutUrl.slice(2) : logoutUrl]);
        }
    }
    async logoutwithoutpath() {
        this.clearTokenRefreshIntervalAndTokens();
        try {
            await ForgRockIdentityProvider.FRUser.logout();
            //location.assign(`${document.location.origin}/`+path);
            sessionStorage.clear();
            this.userService.isAuthenticated = false;
            this.userService.info = null;
        }
        catch (error) {
            console.error(error);
        }
        this.clearLocalStorageAndCookies();
    }
    request(resource, method, data) {
        /** ***********************************************************************
         * SDK INTEGRATION POINT
         * Summary: HttpClient for protected resource server requests.
         * ------------------------------------------------------------------------
         * Details: This helper retrieves your access token from storage and adds
         * it to the authorization header as a bearer token for making HTTP
         * requests to protected resource APIs. It's a wrapper around the native
         * fetch method.
         *********************************************************************** */
        return defer(async () => {
            const response = await ForgRockIdentityProvider.HttpClient.request({
                url: resource,
                init: {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(data),
                    method: method,
                },
                timeout: 5000,
            });
            const json = await response.json();
            // console.log(json);
            return json;
        });
    }
    async checkSessionAndToken(url, realm, clientId) {
        const hasSession = await this.hasSession(url, realm);
        const hasTokens = await this.getToken();
        if (hasSession) {
            if (hasTokens) {
                if (this.tokenHasExpired()) {
                    await this.getUpdatedTokens();
                }
                return Promise.resolve(true);
            }
        }
        else if (hasTokens) {
            // No session but there are still invalid tokens in localStorage,
            // so they must be cleared
            this.clearTokenRefreshIntervalAndTokens();
            this.clearLocalStorageAndCookies();
            // Then reload attempted URL to restart auth flow.
            window.location.reload();
            // Don't allow access to the route
            return Promise.resolve(false);
        }
        // Ensure a return statement for all paths
        return Promise.resolve(false);
    }
    async getUpdatedTokens() {
        // https://backstage.forgerock.com/docs/sdks/latest/_attachments/javascript/api-reference-core/classes/tokenmanager.html
        this.tokens = await ForgRockIdentityProvider.TokenManager.getTokens({
            oauthThreshold: 30000
        });
    }
    async hasSession(url, realm) {
        //const { url, realm } = environment.config.auth.forgeRock;
        const response = await fetch(`${url}/am/json/realms/root/realms/${realm}/sessions/?_action=validate`, {
            method: 'POST',
            credentials: 'include',
            headers: {
                'Accept-API-Version': 'resource=4.0',
                'Content-type': 'application/json',
            },
        });
        const responseJson = (await response.json());
        return responseJson.valid;
    }
    hasTokens(clientId) {
        // NB: The ForgeRock SDK approach is to use await TokenStorage.get()
        // however, being async (for reasons unknown to me), this causes complications
        // in calling hasTokens throughout the apps. Thus the decision was
        // taken to directly reference the access token from localStorage
        // console.info('Checking if tokens are present');
        const tokens = window.sessionStorage.getItem(`FR-SDK-${clientId}`);
        // Whenever the user is already authenticated, the tokens
        // are not set because neither the login nor authorise functions
        // have been called. This ensures the tokens are defined.
        if (!this.tokens?.accessToken && tokens) {
            this.tokens = JSON.parse(tokens);
        }
        //console.info('Tokens present:', tokens);
        return !!tokens;
    }
    async hasToken() {
        // NB: The ForgeRock SDK approach is to use await TokenStorage.get()
        // however, being async (for reasons unknown to me), this causes complications
        // in calling hasTokens throughout the apps. Thus the decision was
        // taken to directly reference the access token from localStorage
        //console.info('Checking if tokens are present');
        //const tokens = await this.getToken();
        const tokens = await ForgRockIdentityProvider.TokenManager.getTokens({
            login: 'redirect'
        });
        //console.log(tokens);
        //this.getUpdatedTokens();
        //console.log(this.tokens);
        // Whenever the user is already authenticated, the tokens
        // are not set because neither the login nor authorise functions
        // have been called. This ensures the tokens are defined.
        if (!this.tokens?.accessToken && tokens) {
            this.tokens = tokens;
        }
        //console.info('Tokens present:', tokens);
        return !!tokens;
    }
    tokenHasExpired() {
        const tokenExpTime = this.getTokenExp();
        const now = getUnixTime(Date.now());
        const remainingTime = differenceInMilliseconds(tokenExpTime, now);
        return remainingTime <= 60;
    }
    getTokenExp() {
        return this.getDecodedToken(this.tokens.accessToken)['exp'];
    }
    getDecodedToken(token) {
        //console.info('Decoding token',token);
        if (!token) {
            throw new Error('Token is undefined');
        }
        return jwtDecode(token);
    }
    clearTokenRefreshIntervalAndTokens() {
        // We've logged out, so stop trying to refresh the access token
        clearInterval(this.tokenUpdater.intervalRef);
        // ForgeRock SDK doesn't know that we keep a copy of the tokens in memory,
        // so when we tell it to log out it clears the tokens from local storage
        // but not from memory, so we have to explicitly clear it here
        this.tokens = {
            accessToken: '',
            idToken: '',
            refreshToken: '',
            tokenExpiry: undefined,
        };
    }
    clearLocalStorageAndCookies() {
        //console.info('Clearing local storage and cookies');
        window.localStorage.clear();
        let COOKIE_DOMAIN = null;
        if (window.location.hostname === 'localhost') {
            COOKIE_DOMAIN = 'localhost';
        }
        else if (window.location.hostname === '.lgnet.co.uk') {
            COOKIE_DOMAIN = '.lgnet.co.uk';
        }
        else {
            COOKIE_DOMAIN = '.landg.com';
        }
        this.cookieService.eraseAll(COOKIE_DOMAIN);
    }
    inIframe() {
        try {
            return window.self !== window.top;
        }
        catch (e) {
            return true;
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.0", ngImport: i0, type: AuthLibService, deps: [{ token: UserService }, { token: CookieService }, { token: i3.Router }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.0", ngImport: i0, type: AuthLibService, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.0", ngImport: i0, type: AuthLibService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: UserService }, { type: CookieService }, { type: i3.Router }] });

class AuthLibComponent {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.0", ngImport: i0, type: AuthLibComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.0", type: AuthLibComponent, isStandalone: true, selector: "lib-auth-lib", ngImport: i0, template: `
    <p>
      auth-lib works!
    </p>
  `, isInline: true, styles: [""] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.0", ngImport: i0, type: AuthLibComponent, decorators: [{
            type: Component,
            args: [{ selector: 'lib-auth-lib', standalone: true, imports: [], template: `
    <p>
      auth-lib works!
    </p>
  ` }]
        }] });

const authGuard = async (router, state) => {
    //info: any;
    const authService = inject(AuthLibService);
    const urlParams = new URLSearchParams(window.location.search);
    let codeParam = urlParams.get('code');
    let stateParam = urlParams.get('state');
    try {
        // const hasTokens = await authService.hasToken();
        const hasTokens = await authService.getToken();
        if (hasTokens) {
            //console.info('Auth Guard: Has Tokens');
            // Check if tokens are still valid (SCRAM scenario only)
            if (authService.tokenHasExpired()) {
                //console.info('Auth Guard: Tokens Have Expired. Renew.');
                await authService.getUpdatedTokens();
            }
            //console.info('Auth Guard: Allow Access');
            return Promise.resolve(true);
        }
        else {
            //console.info('Auth Guard: No Tokens');
            if (codeParam && stateParam) {
                //console.info('Auth Guard: Has Code and State Params');
                //console.info('Auth Guard: Must Authorize');
                if (!authService.inIframe()) {
                    return authService.authorize(codeParam, stateParam);
                }
                else {
                    console.log('Init: in frame');
                    return Promise.resolve(true);
                }
            }
            //console.info('Auth Guard: No Code and State Params');
            //console.info('Auth Guard: Login');
            await authService.authenticate();
            return Promise.resolve(true);
        }
    }
    catch (err) {
        // User likely not authenticated
        //debugger;
        //console.log('In err login');
        //console.log(err);
        throw (err);
    }
};

/*
 * Public API Surface of auth-lib
 */

/**
 * Generated bundle index. Do not edit.
 */

export { AuthLibComponent, AuthLibService, UserService, authGuard };
